import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { respondToNewPasswordRequired } from '../cognitoService';
import { isAllowedRedirectDomain } from '../utils/clientNames';
import Footer from '../components/Footer';

export default function ChangePassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState('');
  const [email, setEmail] = useState('');
  const [redirectToClient, setRedirectToClient] = useState('');
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionParam = urlParams.get('session');
    const emailParam = urlParams.get('email');
    const redirectParam = urlParams.get('redirectToClient');
    
    if (!sessionParam || !emailParam) {
      nav('/login');
      return;
    }
    
    setSession(sessionParam);
    setEmail(emailParam);
    if (redirectParam && isAllowedRedirectDomain(redirectParam)) {
      setRedirectToClient(redirectParam);
    }
  }, [location.search, nav]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one digit';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await respondToNewPasswordRequired({
        email,
        newPassword,
        session,
        redirectToClient
      });
      
      if (redirectToClient) {
        nav('/home');
      } else {
        nav('/home');
      }
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ 
        maxWidth: 400, 
        width: '100%',
        padding: 40,
        backgroundColor: 'white',
        borderRadius: 16,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: 32 }}>
          <img 
            src="/logo.svg" 
            alt="Berg Logo" 
            style={{ 
              width: 120, 
              height: 'auto', 
              marginBottom: 16
            }}
          />
          <h1 style={{ 
            margin: 0, 
            fontSize: 24, 
            fontWeight: 600, 
            color: '#1e293b',
            marginBottom: 8 
          }}>
            Set New Password
          </h1>
          <p style={{ 
            margin: 0, 
            fontSize: 16, 
            color: '#64748b' 
          }}>
            Please create a new password for your account
          </p>
        </div>

        {error && (
          <div style={{ 
            backgroundColor: '#fef2f2', 
            color: '#dc2626', 
            padding: 12, 
            borderRadius: 8, 
            marginBottom: 24,
            fontSize: 14,
            border: '1px solid #fecaca',
            textAlign: 'left'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'block', 
              fontSize: 14, 
              fontWeight: 500, 
              color: '#374151', 
              marginBottom: 6 
            }}>
              New Password
            </label>
            <input 
              type="password" 
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)} 
              required 
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 16,
                backgroundColor: loading ? '#f9fafb' : '#ffffff',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
            <div style={{
              fontSize: 12,
              color: '#64748b',
              marginTop: 4
            }}>
              Minimum 6 characters, 1 digit, 1 uppercase letter, 1 special character
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ 
              display: 'block', 
              fontSize: 14, 
              fontWeight: 500, 
              color: '#374151', 
              marginBottom: 6 
            }}>
              Confirm Password
            </label>
            <input 
              type="password" 
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} 
              required 
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 16,
                backgroundColor: loading ? '#f9fafb' : '#ffffff',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: loading ? '#94a3b8' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
            onMouseOver={e => {
              if (!loading) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseOut={e => {
              if (!loading) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            {loading && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: 'spin 1s linear infinite'
                }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {loading ? 'Setting Password...' : 'Set Password'}
          </button>
          <style>
            {`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </form>
      </div>

      <Footer />
    </div>
  );
}